import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Badge,
  Dropdown,
  Drawer,
  Typography,
  Space,
  Select,
} from 'antd';
import {
  MenuOutlined,
  ShoppingCartOutlined,
  BellOutlined,
  UserOutlined,
  ShopOutlined,
  HeartOutlined,
  WalletOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  MessageOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  GiftOutlined,
  EnvironmentOutlined,
  LockOutlined,
  StarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { cartService, notificationService, currencyService } from '../services/api';
import WhatsAppButton from './WhatsAppButton';

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

const CustomerLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchCartCount();
    fetchNotifCount();

    const onFocus = () => {
      fetchCartCount();
      fetchNotifCount();
    };

    const interval = setInterval(fetchNotifCount, 30000);

    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('NPR');

  useEffect(() => {
    currencyService
      .getCurrencies()
      .then((res) => {
        const list = res.data || [];
        setCurrencies(list);
        const def = list.find((c) => c.is_default);
        if (def) setSelectedCurrency(def.code);
      })
      .catch(() => {});
  }, []);

  const fetchCartCount = async () => {
    try {
      const response = await cartService.getCart();
      const items = response.data?.items || [];
      setCartCount(items.length);
    } catch {
      setCartCount(0);
    }
  };

  const fetchNotifCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setNotifCount(response.data?.unread_count || 0);
    } catch {
      setNotifCount(0);
    }
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">Dashboard</Link>,
    },
    {
      key: '/customer/products',
      icon: <ShopOutlined />,
      label: <Link to="/customer/products">Products</Link>,
    },
    { key: '/orders', icon: <FileTextOutlined />, label: <Link to="/orders">Orders</Link> },
    { key: '/bookings', icon: <FileTextOutlined />, label: <Link to="/bookings">Bookings</Link> },
    { key: '/wishlist', icon: <HeartOutlined />, label: <Link to="/wishlist">Wishlist</Link> },
    { key: '/cart', icon: <ShoppingCartOutlined />, label: <Link to="/cart">Cart</Link> },
    { key: '/wallet', icon: <WalletOutlined />, label: <Link to="/wallet">Wallet</Link> },
    {
      key: '/referral',
      icon: <ThunderboltOutlined />,
      label: <Link to="/referral">Referral</Link>,
    },
    {
      key: '/rewards',
      icon: <GiftOutlined />,
      label: <Link to="/rewards">Rewards</Link>,
    },
    {
      key: '/membership',
      icon: <CrownOutlined />,
      label: <Link to="/membership">Membership</Link>,
    },
    {
      key: '/gift-certificates',
      icon: <GiftOutlined />,
      label: <Link to="/gift-certificates">Gift Certificates</Link>,
    },
    {
      key: '/affiliate',
      icon: <ThunderboltOutlined />,
      label: <Link to="/affiliate">Affiliate</Link>,
    },
    { key: '/chat', icon: <MessageOutlined />, label: <Link to="/chat">Chat</Link> },
  ];

  const profileMenuItems = [
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: <Link to="/profile">My Profile</Link>,
    },
    {
      key: '/addresses',
      icon: <EnvironmentOutlined />,
      label: <Link to="/addresses">Addresses</Link>,
    },
    {
      key: '/payment-methods',
      icon: <WalletOutlined />,
      label: <Link to="/payment-methods">Payment Methods</Link>,
    },
    {
      key: '/two-factor-auth',
      icon: <LockOutlined />,
      label: <Link to="/two-factor-auth">Security (2FA)</Link>,
    },
    {
      key: '/my-reviews',
      icon: <StarOutlined />,
      label: <Link to="/my-reviews">My Reviews</Link>,
    },
    {
      key: '/coupons',
      icon: <GiftOutlined />,
      label: <Link to="/coupons">Coupons</Link>,
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: <Link to="/settings">Settings</Link>,
    },
    { type: 'divider' },
    {
      key: 'help-support',
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help-support">Help & Support</Link>,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f5f0' }}>
      <Header
        style={{
          background: 'linear-gradient(135deg, #2d5a3d 0%, #3d7a4d 50%, #52b788 100%)',
          height: '64px',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 12px rgba(45, 90, 61, 0.3)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Space>
          <Button
            type="text"
            icon={<MenuOutlined style={{ color: 'white', fontSize: '18px' }} />}
            onClick={() => {
              if (window.innerWidth < 768) {
                setMobileDrawerVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            className="sidebar-trigger-btn"
            style={{ alignItems: 'center', justifyContent: 'center' }}
          />
          <Link
            to="/dashboard"
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <img
              src="/images/currentcare-logo.png"
              alt="Current Care"
              className="customer-header-logo"
              onError={(e) => {
                e.target.src =
                  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
              }}
            />
            <Text
              strong
              style={{ color: 'white', fontSize: '18px', letterSpacing: '0.5px' }}
              className="hidden sm:inline"
            >
              Current Care
            </Text>
          </Link>
        </Space>

        <Space size="small" style={{ gap: 2 }}>
          {/* Currency Selector */}
          {currencies.length > 1 && (
            <Select
              value={selectedCurrency}
              onChange={setSelectedCurrency}
              style={{ width: 70 }}
              size="small"
              variant="borderless"
              className="hidden md:inline-flex"
              options={currencies
                .filter((c) => c.is_active)
                .map((c) => ({
                  value: c.code,
                  label: `${c.symbol} ${c.code}`,
                }))}
            />
          )}
          <Badge count={notifCount} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<BellOutlined style={{ color: 'white', fontSize: '16px' }} />}
              onClick={() => navigate('/notifications')}
            />
          </Badge>
          <Badge count={cartCount} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<ShoppingCartOutlined style={{ color: 'white', fontSize: '16px' }} />}
              onClick={() => navigate('/cart')}
            />
          </Badge>
          <Dropdown menu={{ items: profileMenuItems }} trigger={['click']} placement="bottomRight">
            <div
              className="customer-profile-pill"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 10px 4px 4px',
                marginLeft: '4px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.15)',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.28)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              <Avatar
                size={30}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#a8e6cf', color: '#2d5a3d' }}
              />
              <Text
                strong
                style={{ color: 'white', fontSize: '13px', maxWidth: 120 }}
                className="hidden sm:inline"
                ellipsis
              >
                {user?.name || user?.full_name || 'User'}
              </Text>
            </div>
          </Dropdown>
        </Space>
      </Header>

      <Layout>
        <Sider
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth={0}
          style={{
            background: 'white',
            borderRight: '1px solid #e8f0e8',
            boxShadow: '2px 0 12px rgba(0,0,0,0.05)',
          }}
        >
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={() => {
              if (window.innerWidth < 992) setCollapsed(true);
            }}
            style={{ borderRight: 0, padding: '8px 0' }}
          />
        </Sider>

        <Drawer
          title={<span style={{ color: '#2d5a3d' }}>Navigation</span>}
          placement="left"
          onClose={() => setMobileDrawerVisible(false)}
          open={mobileDrawerVisible}
          width={250}
          styles={{ body: { padding: 0 } }}
        >
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={() => setMobileDrawerVisible(false)}
            style={{ borderRight: 0 }}
          />
        </Drawer>

        <Content
          style={{ padding: '24px', background: '#f0f5f0', minHeight: 'calc(100vh - 64px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <Outlet />
          </div>
        </Content>
      </Layout>

      <Footer
        style={{
          background: 'linear-gradient(135deg, #2d5a3d, #1a3a2a)',
          padding: '16px 24px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
            &copy; {new Date().getFullYear()} Current Care. All rights reserved. &nbsp;|&nbsp;
            <Link to="/privacy" style={{ color: 'rgba(168,230,207,0.8)', margin: '0 8px' }}>
              Privacy
            </Link>
            <Link to="/terms" style={{ color: 'rgba(168,230,207,0.8)', margin: '0 8px' }}>
              Terms
            </Link>
          </Text>
        </div>
      </Footer>
      <WhatsAppButton />
    </Layout>
  );
};

export default CustomerLayout;
